import { AlertTriangle, Calculator, Info } from "lucide-react";
import type { ScoringBreakdown as ScoringBreakdownType } from "../../types/admin.types";
import { Badge } from "../ui/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";

interface ScoringBreakdownProps {
  data: ScoringBreakdownType;
}

type CriteriaResult = "perfect" | "good" | "partial" | "mismatch" | "unknown" | string;
type BadgeVariant = "success" | "info" | "warning" | "error" | "default";

function getCriteriaResultVariant(result: CriteriaResult): BadgeVariant {
  const variantMap: Record<string, BadgeVariant> = {
    perfect: "success",
    good: "info",
    partial: "warning",
    mismatch: "error",
    unknown: "default",
  };
  return variantMap[result] ?? "default";
}

/** マッチレベルに応じた色を返す */
function getMatchLevelColor(level: number): string {
  if (level >= 5) return "text-green-600";
  if (level >= 4) return "text-blue-600";
  if (level >= 3) return "text-yellow-600";
  if (level >= 2) return "text-orange-600";
  return "text-red-600";
}

/** マッチレベルに応じた背景グラデーションを返す */
function getMatchLevelGradient(level: number): string {
  if (level >= 5) return "from-green-50 to-emerald-50";
  if (level >= 4) return "from-blue-50 to-purple-50";
  if (level >= 3) return "from-yellow-50 to-amber-50";
  if (level >= 2) return "from-orange-50 to-amber-50";
  return "from-red-50 to-rose-50";
}

export function ScoringBreakdown({ data }: ScoringBreakdownProps) {
  const { version, contentBasedScore, criteriaScore, finalScore } = data;

  // 全項目がunknownかどうかをチェック（警告表示用）
  const allUnknown =
    criteriaScore !== null &&
    criteriaScore.results.length > 0 &&
    criteriaScore.results.every((r) => r.result === "unknown");

  // 判定可能な項目数をカウント
  const validCriteriaCount =
    criteriaScore?.results.filter((r) => r.result !== "unknown").length ?? 0;

  return (
    <div className="space-y-6">
      {/* 最終マッチレベル（SCORE-001） */}
      <Card className={`bg-gradient-to-r ${getMatchLevelGradient(finalScore.restrictedLevel)}`}>
        <CardContent className="py-8">
          <div className="text-center">
            <div className="mb-2 flex items-center justify-center gap-2">
              <p className="text-gray-500 text-sm">最終マッチレベル</p>
              <Badge className="text-xs" variant="default">
                v{version}
              </Badge>
            </div>
            <div className="flex items-center justify-center gap-4">
              <span
                className={`font-bold text-7xl ${getMatchLevelColor(finalScore.restrictedLevel)}`}
              >
                {finalScore.restrictedLevel}
              </span>
              <span className="text-3xl text-gray-400">/ 5</span>
            </div>
            {/* レベル制限が適用された場合の表示（SCORE-008） */}
            {finalScore.calculatedLevel !== finalScore.restrictedLevel && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-2 text-orange-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">
                  計算値 Lv.{finalScore.calculatedLevel} → 制限適用後 Lv.
                  {finalScore.restrictedLevel}
                </span>
              </div>
            )}
            <div className="mt-4 inline-block rounded-lg bg-white/60 px-4 py-2 font-mono text-gray-600 text-sm backdrop-blur-sm">
              {finalScore.formula}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 全項目unknown警告（scoring-logic.md エッジケース対応） */}
      {allUnknown && (
        <Card className="border-orange-300 bg-orange-50">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
            <div>
              <p className="font-medium text-orange-800">全判定項目が判定不能（unknown）</p>
              <p className="mt-1 text-orange-600 text-sm">
                全てのカスタム判定項目が「unknown」のため、重み付けスコアが0.0として計算されています。
                これにより最終スコアが低く評価されている可能性があります。
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* コンテンツベース評価（SCORE-002, SCORE-003, SCORE-004, SCORE-005） */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Calculator className="h-5 w-5 text-blue-600" />
                コンテンツベース評価
              </span>
              <Badge variant={criteriaScore ? "info" : "success"}>
                {criteriaScore ? "30%" : "100%"} 重み付け
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* 必須スキルマッチ率（SCORE-003） */}
            <ScoreBar
              color="red"
              description="業務遂行に必須の技術スキル"
              label="必須スキルマッチ率"
              value={contentBasedScore.requiredSkillsMatchRate}
              weight={60}
            />
            {/* 歓迎スキルマッチ率（SCORE-004） */}
            <ScoreBar
              color="blue"
              description="あると望ましいスキル"
              label="歓迎スキルマッチ率"
              value={contentBasedScore.preferredSkillsMatchRate}
              weight={30}
            />
            {/* 経験マッチ率（SCORE-005） */}
            <ScoreBar
              color="purple"
              description="実務経験の一致度"
              label="経験マッチ率"
              value={contentBasedScore.experienceMatchRate}
              weight={10}
            />

            <div className="border-gray-200 border-t pt-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">コンテンツベーススコア</span>
                <span className="font-bold text-gray-900 text-xl">
                  {(contentBasedScore.weightedScore * 100).toFixed(1)}%
                </span>
              </div>
              <div className="mt-2 rounded-md bg-gray-50 p-2 font-mono text-gray-600 text-xs">
                <span className="text-gray-400">
                  // contentScore = required×0.6 + preferred×0.3 + exp×0.1
                </span>
                <br />= {(contentBasedScore.requiredSkillsMatchRate * 100).toFixed(0)}% × 0.6 +{" "}
                {(contentBasedScore.preferredSkillsMatchRate * 100).toFixed(0)}% × 0.3 +{" "}
                {(contentBasedScore.experienceMatchRate * 100).toFixed(0)}% × 0.1
                <br />={" "}
                <span className="font-semibold text-blue-600">
                  {(contentBasedScore.weightedScore * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* カスタム判定評価（SCORE-006, SCORE-007） */}
        {criteriaScore && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-green-600" />
                  カスタム判定評価
                </span>
                <Badge variant="success">70% 重み付け</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 判定可能項目数の表示 */}
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Info className="h-4 w-4" />
                <span>
                  判定可能: {validCriteriaCount} / {criteriaScore.results.length} 項目
                </span>
              </div>

              <div className="space-y-3">
                {criteriaScore.results.map((result, index) => (
                  <div
                    className={`flex items-start gap-3 rounded-lg p-3 ${
                      result.result === "unknown" ? "bg-gray-100" : "bg-gray-50"
                    }`}
                    key={index}
                  >
                    <Badge
                      className="mt-0.5 shrink-0"
                      variant={getCriteriaResultVariant(result.result)}
                    >
                      {result.result}
                      <span className="ml-1 text-xs opacity-75">
                        ({getResultScoreDisplay(result.result)})
                      </span>
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm">{result.criteriaText}</p>
                      <p className="mt-1 line-clamp-2 text-gray-500 text-xs">{result.reason}</p>
                    </div>
                    <Badge
                      className={`shrink-0 text-xs ${getImportanceBadgeClass(result.importance)}`}
                      variant="default"
                    >
                      {result.importance}
                      <span className="ml-1 opacity-75">
                        (×{getImportanceWeight(result.importance)})
                      </span>
                    </Badge>
                  </div>
                ))}
              </div>

              <div className="border-gray-200 border-t pt-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">カスタム判定スコア</span>
                  <span className="font-bold text-gray-900 text-xl">
                    {(criteriaScore.weightedScore * 100).toFixed(1)}%
                  </span>
                </div>
                <p className="mt-1 text-gray-500 text-xs">
                  ※ 重要度（critical=4, high=3, normal=2, low=1）で重み付け計算
                </p>
              </div>

              {/* レベル制限の表示（SCORE-008） */}
              {criteriaScore.levelRestriction !== null && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
                    <div>
                      <p className="font-semibold text-orange-800">
                        レベル制限: Lv.{criteriaScore.levelRestriction} 以下に制限
                      </p>
                      <p className="mt-1 text-orange-600 text-sm">
                        {criteriaScore.restrictionReason}
                      </p>
                      <div className="mt-2 rounded bg-orange-100 p-2 text-orange-700 text-xs">
                        <p className="font-medium">制限ルール参照:</p>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4">
                          <li>critical mismatch ×1 → Lv.2以下</li>
                          <li>high mismatch ×2 → Lv.3以下</li>
                          <li>normal mismatch ×3 → Lv.4以下</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* カスタム判定なしの場合の説明 */}
        {!criteriaScore && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Info className="mb-3 h-10 w-10 text-gray-300" />
              <p className="font-medium text-gray-600">カスタム判定項目なし</p>
              <p className="mt-1 text-gray-400 text-sm">
                この求人にはカスタム判定項目が設定されていないため、
                <br />
                コンテンツベース評価100%で計算されています。
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* 統合スコア計算式（SCORE-009） */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-purple-600" />
            統合スコア計算
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-gradient-to-r from-purple-50 to-blue-50 p-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">統合スコア</span>
              <span className="font-bold text-2xl text-purple-700">
                {(finalScore.weightedScore * 100).toFixed(1)}%
              </span>
            </div>
            <div className="mt-3 font-mono text-gray-600 text-sm">
              {criteriaScore ? (
                <>
                  <span className="text-gray-400">// カスタム判定あり: 70% + 30%</span>
                  <br />
                  finalScore = criteriaScore × 0.7 + contentScore × 0.3
                  <br />= {(criteriaScore.weightedScore * 100).toFixed(1)}% × 0.7 +{" "}
                  {(contentBasedScore.weightedScore * 100).toFixed(1)}% × 0.3
                  <br />={" "}
                  <span className="font-semibold text-purple-600">
                    {(finalScore.weightedScore * 100).toFixed(1)}%
                  </span>
                </>
              ) : (
                <>
                  <span className="text-gray-400">// カスタム判定なし: コンテンツベース 100%</span>
                  <br />
                  finalScore = contentScore
                  <br />={" "}
                  <span className="font-semibold text-purple-600">
                    {(finalScore.weightedScore * 100).toFixed(1)}%
                  </span>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 計算過程のコード形式表示（SCORE-010） */}
      <Card>
        <CardHeader>
          <CardTitle>計算過程（コード形式）</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 overflow-x-auto rounded-lg bg-gray-900 p-4 font-mono text-gray-100 text-sm">
            <p className="text-gray-500">// ========== Step A: マッチ率計算 ==========</p>
            <p>
              <span className="text-blue-400">const</span> requiredSkillsMatchRate ={" "}
              <span className="text-yellow-300">
                {(contentBasedScore.requiredSkillsMatchRate * 100).toFixed(1)}%
              </span>
            </p>
            <p>
              <span className="text-blue-400">const</span> preferredSkillsMatchRate ={" "}
              <span className="text-yellow-300">
                {(contentBasedScore.preferredSkillsMatchRate * 100).toFixed(1)}%
              </span>
            </p>
            <p>
              <span className="text-blue-400">const</span> experienceMatchRate ={" "}
              <span className="text-yellow-300">
                {(contentBasedScore.experienceMatchRate * 100).toFixed(1)}%
              </span>
            </p>
            <p className="text-gray-500" />
            <p className="text-gray-500">// ========== Step B: コンテンツベーススコア ==========</p>
            <p>
              <span className="text-blue-400">const</span> contentScore = required ×{" "}
              <span className="text-green-400">0.6</span> + preferred ×{" "}
              <span className="text-green-400">0.3</span> + exp ×{" "}
              <span className="text-green-400">0.1</span>
            </p>
            <p>
              {" "}
              = {(contentBasedScore.requiredSkillsMatchRate * 100).toFixed(1)} × 0.6 +{" "}
              {(contentBasedScore.preferredSkillsMatchRate * 100).toFixed(1)} × 0.3 +{" "}
              {(contentBasedScore.experienceMatchRate * 100).toFixed(1)} × 0.1
            </p>
            <p>
              {" "}
              ={" "}
              <span className="text-cyan-400">
                {(contentBasedScore.weightedScore * 100).toFixed(1)}%
              </span>
            </p>
            <p className="text-gray-500" />
            <p className="text-gray-500">// ========== Step C: 統合スコア ==========</p>
            {criteriaScore ? (
              <>
                <p>
                  <span className="text-blue-400">const</span> criteriaScore ={" "}
                  <span className="text-yellow-300">
                    {(criteriaScore.weightedScore * 100).toFixed(1)}%
                  </span>
                </p>
                <p>
                  <span className="text-blue-400">const</span> finalScore = criteriaScore ×{" "}
                  <span className="text-green-400">0.7</span> + contentScore ×{" "}
                  <span className="text-green-400">0.3</span>
                </p>
                <p>
                  {" "}
                  = {(criteriaScore.weightedScore * 100).toFixed(1)} × 0.7 +{" "}
                  {(contentBasedScore.weightedScore * 100).toFixed(1)} × 0.3
                </p>
                <p>
                  {" "}
                  ={" "}
                  <span className="text-cyan-400">
                    {(finalScore.weightedScore * 100).toFixed(1)}%
                  </span>
                </p>
              </>
            ) : (
              <>
                <p>
                  <span className="text-gray-500">
                    // カスタム判定項目なし → コンテンツベース 100%
                  </span>
                </p>
                <p>
                  <span className="text-blue-400">const</span> finalScore = contentScore ={" "}
                  <span className="text-cyan-400">
                    {(finalScore.weightedScore * 100).toFixed(1)}%
                  </span>
                </p>
              </>
            )}
            <p className="text-gray-500" />
            <p className="text-gray-500">// ========== Step D: レベル変換 ==========</p>
            <p>
              <span className="text-gray-500">
                // scoreToLevel: 0-20%→Lv.1, 20-40%→Lv.2, 40-60%→Lv.3, 60-80%→Lv.4, 80-100%→Lv.5
              </span>
            </p>
            <p>
              <span className="text-blue-400">const</span> calculatedLevel =
              scoreToLevel(finalScore) ={" "}
              <span className="text-yellow-300">Lv.{finalScore.calculatedLevel}</span>
            </p>
            <p className="text-gray-500" />
            {finalScore.calculatedLevel !== finalScore.restrictedLevel ? (
              <>
                <p className="text-gray-500">// ========== Step E: レベル制限適用 ==========</p>
                <p>
                  <span className="text-orange-400">// {criteriaScore?.restrictionReason}</span>
                </p>
                <p>
                  <span className="text-blue-400">const</span> restrictedLevel = Math.min(
                  {finalScore.calculatedLevel}, {criteriaScore?.levelRestriction}) ={" "}
                  <span className="text-red-400">Lv.{finalScore.restrictedLevel}</span>
                </p>
              </>
            ) : (
              <>
                <p className="text-gray-500">// ========== Step E: レベル制限なし ==========</p>
                <p>
                  <span className="text-blue-400">const</span> finalLevel ={" "}
                  <span className="text-green-400">Lv.{finalScore.restrictedLevel}</span>
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* マッチレベル変換参照表 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-gray-600" />
            マッチレベル変換参照
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-2">
            {[
              { level: 1, range: "0-20%", color: "bg-red-100 border-red-300 text-red-700" },
              {
                level: 2,
                range: "20-40%",
                color: "bg-orange-100 border-orange-300 text-orange-700",
              },
              {
                level: 3,
                range: "40-60%",
                color: "bg-yellow-100 border-yellow-300 text-yellow-700",
              },
              { level: 4, range: "60-80%", color: "bg-blue-100 border-blue-300 text-blue-700" },
              { level: 5, range: "80-100%", color: "bg-green-100 border-green-300 text-green-700" },
            ].map(({ level, range, color }) => (
              <div
                className={`rounded-lg border p-3 text-center ${color} ${
                  level === finalScore.restrictedLevel ? "ring-2 ring-blue-500 ring-offset-2" : ""
                }`}
                key={level}
              >
                <p className="font-bold text-xl">Lv.{level}</p>
                <p className="text-sm">{range}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-center text-gray-500 text-sm">
            ※ 境界値は上位レベルに含まれます（例: 80%はLv.5）
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

interface ScoreBarProps {
  label: string;
  value: number;
  weight: number;
  color: "red" | "blue" | "green" | "purple";
  description?: string;
}

const colorClasses = {
  red: "bg-red-500",
  blue: "bg-blue-500",
  green: "bg-green-500",
  purple: "bg-purple-500",
};

const bgColorClasses = {
  red: "bg-red-100",
  blue: "bg-blue-100",
  green: "bg-green-100",
  purple: "bg-purple-100",
};

function ScoreBar({ label, value, weight, color, description }: ScoreBarProps) {
  const percentage = value * 100;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <div>
          <span className="font-medium text-gray-700 text-sm">{label}</span>
          {description && <span className="ml-2 text-gray-400 text-xs">{description}</span>}
        </div>
        <span className="text-gray-600 text-sm">
          <span className="font-semibold">{percentage.toFixed(0)}%</span>
          <span className="ml-1 text-gray-400 text-xs">(×{weight}%)</span>
        </span>
      </div>
      <div className={`h-4 overflow-hidden rounded-full ${bgColorClasses[color]}`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ${colorClasses[color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="mt-1 flex justify-between text-gray-400 text-xs">
        <span>0%</span>
        <span>寄与: {((percentage * weight) / 100).toFixed(1)}%</span>
        <span>100%</span>
      </div>
    </div>
  );
}

/** 判定結果のスコア表示を取得（DB保存時と同じ値を使用） */
function getResultScoreDisplay(result: string): string {
  // RESULT_SCORES from criteria-result.vo.ts と一致させる
  const scores: Record<string, string> = {
    perfect: "100%", // 1.0
    good: "75%", // 0.75
    partial: "50%", // 0.5
    mismatch: "25%", // 0.25
    unknown: "除外", // null（計算から除外）
  };
  return scores[result] ?? "N/A";
}

/** 重要度の重み数値を取得 */
function getImportanceWeight(importance: string): number {
  const weights: Record<string, number> = {
    critical: 4,
    high: 3,
    normal: 2,
    low: 1,
  };
  return weights[importance] ?? 1;
}

/** 重要度に応じたバッジスタイルを返す */
function getImportanceBadgeClass(importance: string): string {
  const classes: Record<string, string> = {
    critical: "bg-red-100 text-red-700 border-red-200",
    high: "bg-orange-100 text-orange-700 border-orange-200",
    normal: "bg-gray-100 text-gray-700 border-gray-200",
    low: "bg-gray-50 text-gray-500 border-gray-100",
  };
  return classes[importance] ?? "";
}
